import React, { useState } from "react";
import PageTopBar from "../../../../components/common/PageTopBar";
import { toolbarButtonSx } from "../../../../components/common/toolbarStyles";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useSections } from "../../hooks/useSections";

const generateCode = (name: string) => name.trim().replace(/\s+/g, "_");

/** `tabs`: when shown inside Academics → Setup, the tabs replace the title in the top bar. */
const SectionsUI: React.FC<{ tabs?: React.ReactNode }> = ({ tabs }) => {
  const { sections, loading, creating, error, isDuplicateName, actions } = useSections();

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  const startAdding = () => {
    setAdding(true);
    setName("");
    setDescription("");
    setNameError(null);
  };

  const cancelAdding = () => {
    setAdding(false);
    setName("");
    setDescription("");
    setNameError(null);
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Name is required.");
      return;
    }
    if (isDuplicateName(trimmedName)) {
      setNameError("A section with this name already exists.");
      return;
    }

    const success = await actions.create(generateCode(trimmedName), trimmedName, description);
    if (success) {
      cancelAdding();
    }
  };

  return (
    <Box>
      <PageTopBar
        left={tabs ?? "Sections"}
        right={
          <Button variant="contained" size="small" sx={toolbarButtonSx} startIcon={<AddIcon />} onClick={startAdding} disabled={adding}>
            Add Section
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              {adding && <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : sections.length === 0 && !adding ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No sections found. Create one below.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sections.map((section) => (
                <TableRow key={section.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                  <TableCell>{section.name}</TableCell>
                  <TableCell>{section.code}</TableCell>
                  <TableCell>{section.description}</TableCell>
                  {adding && <TableCell />}
                </TableRow>
              ))
            )}

            {adding && (
              <TableRow>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (nameError) setNameError(null);
                    }}
                    error={Boolean(nameError)}
                    helperText={nameError}
                    fullWidth
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField size="small" value={generateCode(name)} disabled fullWidth />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField
                    size="small"
                    placeholder="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                  />
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: "top" }}>
                  <IconButton size="small" color="primary" onClick={handleSave} disabled={creating}>
                    {creating ? <CircularProgress size={18} /> : <CheckIcon fontSize="small" />}
                  </IconButton>
                  <IconButton size="small" onClick={cancelAdding} disabled={creating}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SectionsUI;
